import { CatalogProduct, ProductFamily, productFamily } from '../utils/catalogProducts';

export type PurposeId = 'light' | 'vent' | 'walk' | 'exit';
export type ShapeId = 'rect' | 'round' | 'band';
export type ScaleId = 'single' | 'generous' | 'span';

export type NeedsAnswers = {
  purpose?: PurposeId;
  shape?: ShapeId;
  scale?: ScaleId;
};

export type NeedChoice<T extends string> = {
  id: T;
  label: string;
  hint: string;
};

export type NeedQuestion<K extends keyof NeedsAnswers, T extends string> = {
  key: K;
  prompt: string;
  choices: NeedChoice<T>[];
};

export type RankedNeedProduct = {
  product: CatalogProduct;
  family: ProductFamily;
  score: number;
  reason: string;
};

const PURPOSE_CHOICES: NeedChoice<PurposeId>[] = [
  {
    id: 'light',
    label: 'Tageslicht',
    hint: 'Fest verglast. Das Fenster bleibt geschlossen.',
  },
  {
    id: 'vent',
    label: 'Tageslicht und Lüften',
    hint: 'Elektrisch öffenbar, mit Motor und Regensensor.',
  },
  {
    id: 'walk',
    label: 'Begehbare Glasfläche',
    hint: 'Glas, auf das man treten kann.',
  },
  {
    id: 'exit',
    label: 'Dachausstieg',
    hint: 'Zugang zum Dach, Serviceöffnung, optional Rauchabzug.',
  },
];

const SHAPE_CHOICES: NeedChoice<ShapeId>[] = [
  {
    id: 'rect',
    label: 'Rechteckig',
    hint: 'Klassische Dachöffnung, auf Bestellmaß.',
  },
  {
    id: 'round',
    label: 'Rund',
    hint: 'Für Treppenauge, Foyer oder Lichthof.',
  },
  {
    id: 'band',
    label: 'Als Lichtband',
    hint: 'Mehrere Meter, modular im 1-Meter-Raster.',
  },
];

const SCALE_CHOICES: NeedChoice<ScaleId>[] = [
  {
    id: 'single',
    label: 'Ein einzelnes Fenster',
    hint: 'Eine kompakte Öffnung an einer Stelle.',
  },
  {
    id: 'generous',
    label: 'Eine großzügige Öffnung',
    hint: 'Ein großes Fenster, nicht als Band.',
  },
  {
    id: 'span',
    label: 'Über mehrere Meter',
    hint: 'Eine lange Lichtlinie im Dach.',
  },
];

const FAMILY_FOR_PURPOSE: Record<PurposeId, ProductFamily[]> = {
  light: ['fixed', 'circular', 'linear'],
  vent: ['comfort', 'linear', 'access'],
  walk: ['walkon', 'linearWalkon', 'circularWalkon'],
  exit: ['access'],
};

const FAMILY_FOR_SHAPE: Record<ShapeId, ProductFamily[]> = {
  rect: ['fixed', 'comfort', 'walkon', 'linear', 'access'],
  round: ['circular', 'circularWalkon'],
  band: ['linear', 'linearWalkon'],
};

const FAMILY_FOR_SCALE: Record<ScaleId, ProductFamily[]> = {
  single: ['fixed', 'comfort', 'circular', 'walkon', 'access', 'circularWalkon'],
  generous: ['fixed', 'comfort', 'walkon', 'linear', 'linearWalkon'],
  span: ['linear', 'linearWalkon'],
};

function familiesInCatalog(products: CatalogProduct[]): Set<ProductFamily> {
  return new Set(products.map(productFamily));
}

function filterChoices<T extends string>(
  choices: NeedChoice<T>[],
  available: Set<ProductFamily>,
  map: Record<T, ProductFamily[]>,
): NeedChoice<T>[] {
  return choices.filter((choice) => map[choice.id].some((family) => available.has(family)));
}

export function needsQuestions(products: CatalogProduct[]): Array<
  | NeedQuestion<'purpose', PurposeId>
  | NeedQuestion<'shape', ShapeId>
  | NeedQuestion<'scale', ScaleId>
> {
  const available = familiesInCatalog(products);
  const questions: Array<
    | NeedQuestion<'purpose', PurposeId>
    | NeedQuestion<'shape', ShapeId>
    | NeedQuestion<'scale', ScaleId>
  > = [];

  const purpose = filterChoices(PURPOSE_CHOICES, available, FAMILY_FOR_PURPOSE);
  if (purpose.length > 1) {
    questions.push({
      key: 'purpose',
      prompt: 'Was soll das Fenster leisten?',
      choices: purpose,
    });
  }

  const shape = filterChoices(SHAPE_CHOICES, available, FAMILY_FOR_SHAPE);
  if (shape.length > 1) {
    questions.push({
      key: 'shape',
      prompt: 'Welche Form stellen Sie sich vor?',
      choices: shape,
    });
  }

  const scale = filterChoices(SCALE_CHOICES, available, FAMILY_FOR_SCALE);
  if (scale.length > 1) {
    questions.push({
      key: 'scale',
      prompt: 'Wie groß ungefähr?',
      choices: scale,
    });
  }

  return questions;
}

const SCORE: Record<ProductFamily, { purpose: Record<PurposeId, number>; shape: Record<ShapeId, number>; scale: Record<ScaleId, number> }> = {
  fixed: {
    purpose: { light: 4, vent: -3, walk: -4, exit: -3 },
    shape: { rect: 3, round: -4, band: -2 },
    scale: { single: 2, generous: 2, span: -2 },
  },
  comfort: {
    purpose: { light: 0, vent: 4, walk: -4, exit: 1 },
    shape: { rect: 3, round: -4, band: -1 },
    scale: { single: 2, generous: 2, span: 0 },
  },
  circular: {
    purpose: { light: 3, vent: -2, walk: -4, exit: -4 },
    shape: { rect: -4, round: 6, band: -4 },
    scale: { single: 2, generous: 1, span: -4 },
  },
  circularWalkon: {
    purpose: { light: 0, vent: -3, walk: 6, exit: -4 },
    shape: { rect: -4, round: 6, band: -4 },
    scale: { single: 2, generous: 1, span: -4 },
  },
  walkon: {
    purpose: { light: 0, vent: -2, walk: 6, exit: -3 },
    shape: { rect: 3, round: -4, band: -3 },
    scale: { single: 1, generous: 2, span: -2 },
  },
  linear: {
    purpose: { light: 1, vent: 2, walk: -4, exit: -3 },
    shape: { rect: 0, round: -4, band: 6 },
    scale: { single: -2, generous: 1, span: 5 },
  },
  access: {
    purpose: { light: -2, vent: 1, walk: -4, exit: 6 },
    shape: { rect: 3, round: -4, band: -3 },
    scale: { single: 2, generous: 0, span: -4 },
  },
  linearWalkon: {
    purpose: { light: 0, vent: -3, walk: 5, exit: -3 },
    shape: { rect: 0, round: -4, band: 6 },
    scale: { single: -2, generous: 1, span: 5 },
  },
};

function scoreProduct(family: ProductFamily, answers: NeedsAnswers): number {
  const table = SCORE[family];
  let score = 0;
  if (answers.purpose) score += table.purpose[answers.purpose];
  if (answers.shape) score += table.shape[answers.shape];
  if (answers.scale) score += table.scale[answers.scale];
  return score;
}

function reasonFor(family: ProductFamily, answers: NeedsAnswers): string {
  if (answers.shape === 'round' && family === 'circularWalkon') {
    return 'Rund und begehbar — das ist SkyVision CIRCULAR WALK-ON.';
  }
  if (answers.shape === 'round' && family === 'circular') {
    return 'Rund und fest verglast — das ist SkyVision CIRCULAR.';
  }
  if (answers.shape === 'round' && family === 'comfort') {
    return 'Zum Lüften braucht es ein rechteckiges, öffenbares Fenster.';
  }
  if (answers.purpose === 'walk' && family === 'walkon') {
    return 'Begehbares Glas mit TG-Step, rechteckig auf Bestellmaß.';
  }
  if (answers.purpose === 'walk' && family === 'linearWalkon') {
    return 'Begehbares Lichtband — Module aneinandergereiht, 500 kg/m².';
  }
  if (answers.shape === 'band' && family === 'linearWalkon') {
    return 'Als begehbares Band, nicht als einzelnes WALKON-Fenster.';
  }
  if (answers.purpose === 'exit' && family === 'access') {
    return 'Ausstieg bis 75°, zwei Klapparm-Antriebe, optional als Rauchabzug.';
  }
  if (answers.purpose === 'vent' && family === 'comfort') {
    return 'Elektrisch öffenbar, mit Motor und Regensensor.';
  }
  if (answers.shape === 'band' && family === 'linear') {
    return 'Modulares Lichtband im 1-Meter-Raster, fest und öffenbar kombinierbar.';
  }
  if (answers.scale === 'span' && family === 'linear') {
    return 'Über mehrere Meter trägt ein Bandfenster, kein Einzellelement.';
  }
  if (answers.purpose === 'light' && family === 'fixed') {
    return 'Festverglast, rechteckig, auf Bestellmaß — das ruhige Oberlicht.';
  }
  if (family === 'comfort') {
    return 'Wenn gelüftet werden soll, ist COMFORT das passende Modell.';
  }
  if (family === 'walkon') {
    return 'Wenn die Fläche begehbar sein soll, führt kein Weg an WALKON vorbei.';
  }
  if (family === 'circularWalkon') {
    return 'Die runde begehbare Öffnung gibt es nur bei CIRCULAR WALK-ON.';
  }
  if (family === 'circular') {
    return 'Die runde Öffnung gibt es nur bei CIRCULAR.';
  }
  if (family === 'linear') {
    return 'Für lange Öffnungen im Dach ist LINEAR gebaut.';
  }
  if (family === 'linearWalkon') {
    return 'Für eine begehbare Lichtlinie ist LINEAR WALK-ON gebaut.';
  }
  if (family === 'access') {
    return 'Für den Dachzugang ist ACCESS gebaut — Ausstieg, nicht nur Lüften.';
  }
  return 'Festverglast und rechteckig — der häufigste Fall auf dem Flachdach.';
}

function answeredCount(answers: NeedsAnswers): number {
  return Number(!!answers.purpose) + Number(!!answers.shape) + Number(!!answers.scale);
}

export function rankNeedProducts(
  products: CatalogProduct[],
  answers: NeedsAnswers,
): RankedNeedProduct[] {
  const ranked = products
    .map((product) => {
      const family = productFamily(product);
      return {
        product,
        family,
        score: scoreProduct(family, answers),
        reason: reasonFor(family, answers),
      };
    })
    .sort((a, b) => b.score - a.score);

  if (answeredCount(answers) === 0) return ranked.slice(0, 1);

  const best = ranked[0]?.score ?? 0;
  const positive = ranked.filter((item) => item.score > 0 && item.score >= best - 2);
  const conflictAlt = ranked.filter((item) => {
    if (item.score > 0) return false;
    if (answers.shape === 'round' && answers.purpose === 'vent' && item.family === 'comfort') {
      return true;
    }
    if (answers.shape === 'round' && answers.purpose === 'walk' && item.family === 'circularWalkon') {
      return true;
    }
    if (answers.shape === 'round' && answers.purpose === 'walk' && item.family === 'walkon') {
      return true;
    }
    if (answers.shape === 'band' && answers.purpose === 'walk' && item.family === 'linearWalkon') {
      return true;
    }
    return false;
  });
  const picked = [...positive, ...conflictAlt];
  if (picked.length > 0) return picked.slice(0, 3);

  return ranked.slice(0, 1);
}

export function leadingNeedProduct(
  ranked: RankedNeedProduct[],
): RankedNeedProduct | null {
  return ranked[0] ?? null;
}
